import { useCallback } from 'react';
import { useAiChatStore, AiMessage } from './useAiChatStore';
import axios from '../../api/axios';
import useAuth from '../../utils/hooks/useAuth';

/**
 * Custom hook for AI chat functionality
 * Handles sending messages and receiving AI responses
 */
export const useAiChat = () => {
  const { addMessage, setTyping, conversations, updateConversationTitle } = useAiChatStore();
  const {
    authData: { userData },
  } = useAuth();
  /**
   * Send a message to the AI assistant
   * @param conversationId - The ID of the active conversation
   * @param content - The message content
   * @returns Promise that resolves when the AI response is received
   */
  const sendMessage = useCallback(
    async (conversationId: string, content: string): Promise<void> => {
      if (!content.trim() || !userData?.id) return;

      const userMessage: AiMessage = {
        id: `msg-${Date.now()}`,
        role: 'user',
        content: content.trim(),
        timestamp: new Date(),
      };

      addMessage(conversationId, userMessage);
      setTyping(true);

      try {
        // Send message to AI microservice
        const response = await axios.post('/ai/messages', {
            content: content,
            session_id: Number(conversationId),
          },
          {
            headers: {
              'Content-Type': 'application/json',
              'X-User-ID': String(userData.id),
            },
          },
        );

        // Only update title if this is the first user message
        const conversation = conversations.find((c) => c.id === conversationId);
        if (conversation && conversation.messages.length === 0) {
          const newTitle = content.trim().substring(0, 30) + (content.length > 30 ? '...' : '');
          await axios.patch(
            `/ai/sessions/${Number(conversationId)}`,
            { title: newTitle },
            {
              headers: {
                'Content-Type': 'application/json',
                'X-User-ID': String(userData.id),
              },
            },
          );
          updateConversationTitle(conversationId, newTitle);
        }

        const aiMessage: AiMessage = {
          id: response.data[1].id,
          role: 'assistant',
          content: response.data[1].content,
          timestamp: new Date(response.data[1].created_at),
        };

        addMessage(conversationId, aiMessage);
      } catch (error) {
        console.error('Error sending message to AI:', error);

        const errorMessage: AiMessage = {
          id: `msg-${Date.now()}-error`,
          role: 'assistant',
          content: 'Sorry, I encountered an error. Please try again or contact our support team.',
          timestamp: new Date(),
        };

        addMessage(conversationId, errorMessage);
      } finally {
        setTyping(false);
      }
    },
    [addMessage, setTyping, userData, conversations, updateConversationTitle],
  );

  return { sendMessage };
};
